import { GAME_CONSTANTS } from '@tankworld/shared';
import { STANDARD_TANK_ARMOR_PROFILES } from '@tankworld/armor-physics';
import { angleDifference, clamp, lerp, normalizeAngle } from './math.js';
export class VehiclePhysics {
    id;
    name;
    color;
    isBot;
    tankClassId;
    armorProfile;
    // Position & Orientation
    position;
    velocity;
    hullYaw;
    pitch;
    roll;
    // Track Speeds (m/s)
    leftTrackSpeed = 0;
    rightTrackSpeed = 0;
    forwardSpeed = 0;
    // Turret & Gun
    turretYaw;
    gunPitch = 0;
    // Combat status
    health = GAME_CONSTANTS.MAX_HEALTH;
    isAlive = true;
    respawnTimer = 0;
    reloadTimer = GAME_CONSTANTS.RELOAD_TIME; // Start ready to fire
    selectedAmmoId = 'ap_3bm42';
    // Score
    kills = 0;
    deaths = 0;
    score = 0;
    // Dynamic suspension pitching from acceleration
    accelPitchOffset = 0;
    constructor(id, name, color = '#4b5320', isBot = false, initialPos = { x: 0, y: 0, z: 0 }, initialYaw = 0, tankClassId = 'mbt_standard') {
        this.id = id;
        this.name = name;
        this.color = color;
        this.isBot = isBot;
        this.tankClassId = tankClassId;
        this.armorProfile =
            STANDARD_TANK_ARMOR_PROFILES[tankClassId] || STANDARD_TANK_ARMOR_PROFILES.mbt_standard;
        this.position = { ...initialPos };
        this.velocity = { x: 0, y: 0, z: 0 };
        this.hullYaw = initialYaw;
        this.turretYaw = initialYaw;
        this.pitch = 0;
        this.roll = 0;
    }
    /**
     * Primary fixed-timestep integration
     */
    update(dt, input, terrain) {
        // 1. Respawn timer countdown
        if (!this.isAlive) {
            this.respawnTimer -= dt;
            if (this.respawnTimer <= 0) {
                this.respawn(terrain);
            }
            return;
        }
        // 2. Reload progress
        if (this.reloadTimer < GAME_CONSTANTS.RELOAD_TIME) {
            this.reloadTimer = Math.min(GAME_CONSTANTS.RELOAD_TIME, this.reloadTimer + dt);
        }
        // 3. Differential track throttle commands
        const throttle = clamp(input.throttle, -1, 1);
        const steer = clamp(input.steer, -1, 1);
        const isBraking = input.brake || (throttle === 0 && steer === 0);
        let targetLeftSpeed = 0;
        let targetRightSpeed = 0;
        if (Math.abs(throttle) > 0.05) {
            const maxSpd = throttle > 0 ? GAME_CONSTANTS.MAX_FORWARD_SPEED : GAME_CONSTANTS.MAX_REVERSE_SPEED;
            const baseSpd = throttle * maxSpd;
            // When turning while driving, decrease inner track speed
            const steerFactor = steer * (throttle > 0 ? 0.7 : -0.7);
            targetLeftSpeed = baseSpd * (1 + steerFactor);
            targetRightSpeed = baseSpd * (1 - steerFactor);
        }
        else if (Math.abs(steer) > 0.05) {
            // Neutral pivot turn in place (tracks rotate opposite directions)
            const pivotSpeed = GAME_CONSTANTS.MAX_REVERSE_SPEED * 0.75 * steer;
            targetLeftSpeed = pivotSpeed;
            targetRightSpeed = -pivotSpeed;
        }
        // 4. Inertial track acceleration and braking
        const accelRate = isBraking ? GAME_CONSTANTS.BRAKE_DECELERATION : GAME_CONSTANTS.ACCELERATION;
        const prevSpeed = this.forwardSpeed;
        this.leftTrackSpeed = this.approachSpeed(this.leftTrackSpeed, targetLeftSpeed, accelRate * dt);
        this.rightTrackSpeed = this.approachSpeed(this.rightTrackSpeed, targetRightSpeed, accelRate * dt);
        // Forward speed is average of left and right tracks
        this.forwardSpeed = (this.leftTrackSpeed + this.rightTrackSpeed) * 0.5;
        // Angular yaw velocity derived from track speed difference over gauge
        const trackGauge = GAME_CONSTANTS.TANK_WIDTH * 0.85;
        const yawVelocity = (this.rightTrackSpeed - this.leftTrackSpeed) / trackGauge;
        // 5. Update hull orientation and position
        this.hullYaw = normalizeAngle(this.hullYaw + yawVelocity * dt);
        const fwdX = Math.sin(this.hullYaw);
        const fwdZ = Math.cos(this.hullYaw);
        this.velocity.x = fwdX * this.forwardSpeed;
        this.velocity.z = fwdZ * this.forwardSpeed;
        this.position.x += this.velocity.x * dt;
        this.position.z += this.velocity.z * dt;
        // 6. Terrain height and suspension tilt
        const halfLen = GAME_CONSTANTS.TANK_LENGTH * 0.45;
        const halfWidth = GAME_CONSTANTS.TANK_WIDTH * 0.45;
        const frontX = this.position.x + fwdX * halfLen;
        const frontZ = this.position.z + fwdZ * halfLen;
        const rearX = this.position.x - fwdX * halfLen;
        const rearZ = this.position.z - fwdZ * halfLen;
        const rightX = -fwdZ;
        const rightZ = fwdX;
        const leftX = this.position.x - rightX * halfWidth;
        const leftZ = this.position.z - rightZ * halfWidth;
        const rtX = this.position.x + rightX * halfWidth;
        const rtZ = this.position.z + rightZ * halfWidth;
        const hFront = terrain.getHeight(frontX, frontZ);
        const hRear = terrain.getHeight(rearX, rearZ);
        const hLeft = terrain.getHeight(leftX, leftZ);
        const hRight = terrain.getHeight(rtX, rtZ);
        const hCenter = terrain.getHeight(this.position.x, this.position.z);
        this.position.y = hCenter + 0.35; // Suspension ride height
        // Pitch & roll calculations from ground slope
        const targetGroundPitch = Math.atan2(hFront - hRear, halfLen * 2);
        const targetGroundRoll = Math.atan2(hRight - hLeft, halfWidth * 2);
        // Dynamic nose dip / rise based on acceleration
        const linearAccel = (this.forwardSpeed - prevSpeed) / dt;
        const targetAccelPitch = clamp(-linearAccel * 0.02, -0.15, 0.15);
        this.accelPitchOffset = lerp(this.accelPitchOffset, targetAccelPitch, dt * 6.0);
        this.pitch = lerp(this.pitch, targetGroundPitch + this.accelPitchOffset, dt * 10.0);
        this.roll = lerp(this.roll, targetGroundRoll, dt * 10.0);
        // 7. Turret independent rotation towards desired angle
        const targetTurretYaw = input.turretYaw;
        const turretYawDiff = angleDifference(targetTurretYaw, this.turretYaw);
        const maxTurretStep = GAME_CONSTANTS.TURRET_TURN_SPEED * dt;
        this.turretYaw = normalizeAngle(this.turretYaw + clamp(turretYawDiff, -maxTurretStep, maxTurretStep));
        // 8. Gun elevation / depression
        const targetPitch = clamp(input.gunPitch, GAME_CONSTANTS.GUN_MIN_PITCH, GAME_CONSTANTS.GUN_MAX_PITCH);
        const pitchDiff = targetPitch - this.gunPitch;
        const maxPitchStep = GAME_CONSTANTS.GUN_ELEVATION_SPEED * dt;
        this.gunPitch = clamp(this.gunPitch + clamp(pitchDiff, -maxPitchStep, maxPitchStep), GAME_CONSTANTS.GUN_MIN_PITCH, GAME_CONSTANTS.GUN_MAX_PITCH);
    }
    approachSpeed(current, target, maxDelta) {
        if (Math.abs(target - current) <= maxDelta)
            return target;
        return current + Math.sign(target - current) * maxDelta;
    }
    canFire() {
        return this.isAlive && this.reloadTimer >= GAME_CONSTANTS.RELOAD_TIME;
    }
    onFire() {
        this.reloadTimer = 0;
        // Calculate exact muzzle tip in 3D world space
        const turretHeight = GAME_CONSTANTS.TURRET_HEIGHT_OFFSET;
        const barrelLen = GAME_CONSTANTS.BARREL_LENGTH;
        // Turret orientation in world space
        const cosPitch = Math.cos(this.gunPitch);
        const sinPitch = Math.sin(this.gunPitch);
        const sinYaw = Math.sin(this.turretYaw);
        const cosYaw = Math.cos(this.turretYaw);
        const muzzleDir = {
            x: sinYaw * cosPitch,
            y: sinPitch,
            z: cosYaw * cosPitch,
        };
        const muzzlePos = {
            x: this.position.x + muzzleDir.x * barrelLen,
            y: this.position.y + turretHeight + muzzleDir.y * barrelLen,
            z: this.position.z + muzzleDir.z * barrelLen,
        };
        return { muzzlePos, muzzleDir };
    }
    applyDamage(amount) {
        if (!this.isAlive)
            return false;
        this.health = Math.max(0, this.health - amount);
        if (this.health <= 0) {
            this.isAlive = false;
            this.deaths++;
            this.respawnTimer = GAME_CONSTANTS.RESPAWN_DELAY;
            this.leftTrackSpeed = 0;
            this.rightTrackSpeed = 0;
            this.forwardSpeed = 0;
            return true; // Tank was destroyed
        }
        return false;
    }
    respawn(terrain) {
        this.isAlive = true;
        this.health = GAME_CONSTANTS.MAX_HEALTH;
        this.reloadTimer = GAME_CONSTANTS.RELOAD_TIME;
        this.forwardSpeed = 0;
        this.leftTrackSpeed = 0;
        this.rightTrackSpeed = 0;
        this.pitch = 0;
        this.roll = 0;
        // Pick random spawn point on safe outer ring
        const angle = Math.random() * Math.PI * 2;
        const distance = 80 + Math.random() * 50;
        this.position.x = Math.sin(angle) * distance;
        this.position.z = Math.cos(angle) * distance;
        this.position.y = terrain.getHeight(this.position.x, this.position.z) + 0.35;
        this.hullYaw = normalizeAngle(angle + Math.PI); // Face toward center
        this.turretYaw = this.hullYaw;
    }
    raycastArmor(worldRayStart, worldRayEnd) {
        if (!this.armorProfile || !this.armorProfile.zones)
            return null;
        let closestHit = null;
        for (const zone of this.armorProfile.zones) {
            if (!zone.boundsLocal)
                continue;
            const isTurret = zone.armorId.startsWith('turret_');
            const yaw = isTurret ? this.turretYaw : this.hullYaw;
            const cosY = Math.cos(yaw);
            const sinY = Math.sin(yaw);
            const ox = this.position.x;
            const oy = this.position.y;
            const oz = this.position.z;
            const dx0 = worldRayStart.x - ox;
            const dy0 = worldRayStart.y - oy;
            const dz0 = worldRayStart.z - oz;
            const lx0 = dx0 * cosY - dz0 * sinY;
            const ly0 = dy0;
            const lz0 = dx0 * sinY + dz0 * cosY;
            const dx1 = worldRayEnd.x - ox;
            const dy1 = worldRayEnd.y - oy;
            const dz1 = worldRayEnd.z - oz;
            const lx1 = dx1 * cosY - dz1 * sinY;
            const ly1 = dy1;
            const lz1 = dx1 * sinY + dz1 * cosY;
            const ldx = lx1 - lx0;
            const ldy = ly1 - ly0;
            const ldz = lz1 - lz0;
            const bMin = zone.boundsLocal.min;
            const bMax = zone.boundsLocal.max;
            let tMin = 0;
            let tMax = 1;
            if (Math.abs(ldx) > 1e-6) {
                const tx1 = (bMin.x - lx0) / ldx;
                const tx2 = (bMax.x - lx0) / ldx;
                tMin = Math.max(tMin, Math.min(tx1, tx2));
                tMax = Math.min(tMax, Math.max(tx1, tx2));
            }
            else if (lx0 < bMin.x || lx0 > bMax.x) {
                continue;
            }
            if (Math.abs(ldy) > 1e-6) {
                const ty1 = (bMin.y - ly0) / ldy;
                const ty2 = (bMax.y - ly0) / ldy;
                tMin = Math.max(tMin, Math.min(ty1, ty2));
                tMax = Math.min(tMax, Math.max(ty1, ty2));
            }
            else if (ly0 < bMin.y || ly0 > bMax.y) {
                continue;
            }
            if (Math.abs(ldz) > 1e-6) {
                const tz1 = (bMin.z - lz0) / ldz;
                const tz2 = (bMax.z - lz0) / ldz;
                tMin = Math.max(tMin, Math.min(tz1, tz2));
                tMax = Math.min(tMax, Math.max(tz1, tz2));
            }
            else if (lz0 < bMin.z || lz0 > bMax.z) {
                continue;
            }
            if (tMax >= tMin && tMin <= 1 && tMax >= 0) {
                const tHit = Math.max(0, tMin);
                if (!closestHit || tHit < closestHit.t) {
                    const lHitX = lx0 + ldx * tHit;
                    const lHitY = ly0 + ldy * tHit;
                    const lHitZ = lz0 + ldz * tHit;
                    const wHitX = ox + lHitX * cosY + lHitZ * sinY;
                    const wHitY = oy + lHitY;
                    const wHitZ = oz - lHitX * sinY + lHitZ * cosY;
                    const nLoc = zone.normalLocal;
                    const wNormX = nLoc.x * cosY + nLoc.z * sinY;
                    const wNormY = nLoc.y;
                    const wNormZ = -nLoc.x * sinY + nLoc.z * cosY;
                    const nLen = Math.hypot(wNormX, wNormY, wNormZ) || 1;
                    closestHit = {
                        zone,
                        hitPoint: { x: wHitX, y: wHitY, z: wHitZ },
                        surfaceNormal: { x: wNormX / nLen, y: wNormY / nLen, z: wNormZ / nLen },
                        t: tHit,
                    };
                }
            }
        }
        return closestHit
            ? {
                zone: closestHit.zone,
                hitPoint: closestHit.hitPoint,
                surfaceNormal: closestHit.surfaceNormal,
            }
            : null;
    }
    toState() {
        return {
            id: this.id,
            name: this.name,
            color: this.color,
            isBot: this.isBot,
            position: { ...this.position },
            velocity: { ...this.velocity },
            hullYaw: this.hullYaw,
            pitch: this.pitch,
            roll: this.roll,
            speed: this.forwardSpeed,
            turretYaw: this.turretYaw,
            gunPitch: this.gunPitch,
            health: this.health,
            maxHealth: GAME_CONSTANTS.MAX_HEALTH,
            isAlive: this.isAlive,
            respawnTimer: this.respawnTimer,
            reloadProgress: this.reloadTimer / GAME_CONSTANTS.RELOAD_TIME,
            leftTrackSpeed: this.leftTrackSpeed,
            rightTrackSpeed: this.rightTrackSpeed,
            kills: this.kills,
            deaths: this.deaths,
            score: this.score,
        };
    }
}
//# sourceMappingURL=vehicle.js.map